/**
 * A linear undo/redo history: past snapshots, the current value, and future
 * snapshots (populated only by undoing, discarded the moment a new edit
 * happens) — the standard "past/present/future" shape, kept generic since
 * the logic has nothing to do with `FlowDocument` specifically. Every
 * document mutation in this codebase already returns a brand-new,
 * structurally-immutable object (nodeGraph.ts, edgeGraph.ts, nodePosition.ts,
 * nodeConfig.ts), so pushing whole snapshots is cheap and correct — no diffing,
 * no patches, nothing that could apply cleanly to one document and not
 * another.
 */
export interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

export function initHistory<T>(present: T): HistoryState<T> {
  return { past: [], present, future: [] };
}

/** Records a new present value, discarding any redo-able future. */
export function pushHistory<T>(state: HistoryState<T>, next: T): HistoryState<T> {
  return { past: [...state.past, state.present], present: next, future: [] };
}

export function canUndo<T>(state: HistoryState<T>): boolean {
  return state.past.length > 0;
}

export function canRedo<T>(state: HistoryState<T>): boolean {
  return state.future.length > 0;
}

/** No-op at the start of history — there is nothing further back to go. */
export function undoHistory<T>(state: HistoryState<T>): HistoryState<T> {
  if (state.past.length === 0) {
    return state;
  }
  const previous = state.past[state.past.length - 1]!;
  return { past: state.past.slice(0, -1), present: previous, future: [state.present, ...state.future] };
}

/** No-op once every undone step has been redone — there is nothing ahead. */
export function redoHistory<T>(state: HistoryState<T>): HistoryState<T> {
  if (state.future.length === 0) {
    return state;
  }
  const next = state.future[0]!;
  return { past: [...state.past, state.present], present: next, future: state.future.slice(1) };
}
